import { motion } from 'framer-motion';
import type { GameMode } from '@/types';
import { GAME_CONFIGS } from '@/utils/constants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Timer, Trophy, Zap, Coffee, Medal, Crown } from 'lucide-react';

interface GameModeSelectorProps {
  onSelectMode: (mode: GameMode) => void;
  currentChips: number;
}

const MODE_ICONS: Record<GameMode, React.ReactNode> = {
  casual: <Coffee className="w-6 h-6" />,
  tournament_low: <Medal className="w-6 h-6" />,
  tournament_mid: <Trophy className="w-6 h-6" />,
  tournament_high: <Crown className="w-6 h-6" />,
  turbo: <Zap className="w-6 h-6" />,
};

const MODE_NAMES: Record<GameMode, string> = {
  casual: '休闲场',
  tournament_low: '锦标赛（低）',
  tournament_mid: '锦标赛（中）',
  tournament_high: '锦标赛（高）',
  turbo: '急速赛',
};

const MODE_DESCRIPTIONS: Record<GameMode, string> = {
  casual: '轻松游戏，练手模式，不计入排行榜',
  tournament_low: '入门级锦标赛，适合新手',
  tournament_mid: '中级锦标赛，挑战性更强',
  tournament_high: '高级锦标赛，高手对决',
  turbo: '快节奏比赛，盲注每2分钟翻倍',
};

export default function GameModeSelector({ onSelectMode, currentChips }: GameModeSelectorProps) {
  const modes: GameMode[] = ['casual', 'tournament_low', 'tournament_mid', 'tournament_high', 'turbo'];

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white mb-4">选择游戏模式</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modes.map((mode) => {
          const config = GAME_CONFIGS[mode];
          const canAfford = currentChips >= config.entryFee;
          const isTournament = mode.startsWith('tournament') || mode === 'turbo';

          return (
            <motion.div
              key={mode}
              whileHover={{ scale: canAfford ? 1.02 : 1 }}
              whileTap={{ scale: canAfford ? 0.98 : 1 }}
            >
              <Card 
                className={`bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors cursor-pointer ${
                  !canAfford ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={() => canAfford && onSelectMode(mode)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        mode === 'casual' ? 'bg-green-500/20 text-green-400' :
                        mode === 'tournament_low' ? 'bg-blue-500/20 text-blue-400' :
                        mode === 'tournament_mid' ? 'bg-amber-500/20 text-amber-400' :
                        mode === 'tournament_high' ? 'bg-red-500/20 text-red-400' :
                        'bg-purple-500/20 text-purple-400'
                      }`}>
                        {MODE_ICONS[mode]}
                      </div>
                      <div>
                        <CardTitle className="text-white text-lg">{MODE_NAMES[mode]}</CardTitle>
                        <p className="text-slate-400 text-sm">{MODE_DESCRIPTIONS[mode]}</p>
                      </div>
                    </div>
                    {!canAfford && (
                      <span className="text-red-400 text-xs font-medium">筹码不足</span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="flex items-center gap-1 text-slate-400 mb-1">
                        <Users className="w-4 h-4" />
                        <span>人数</span>
                      </div>
                      <p className="text-white font-medium">{config.minPlayers}-{config.maxPlayers}人</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-slate-400 mb-1">
                        <span className="text-xs">盲注</span>
                      </div>
                      <p className="text-white font-medium">{config.smallBlind}/{config.bigBlind}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-slate-400 mb-1">
                        <span className="text-xs">入场费</span>
                      </div>
                      <p className={`font-medium ${config.entryFee === 0 ? 'text-green-400' : 'text-amber-400'}`}>
                        {config.entryFee === 0 ? '免费' : config.entryFee.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  {isTournament && (
                    <div className="mt-3 pt-3 border-t border-slate-700/50 flex items-center gap-4 text-xs text-slate-400">
                      <div className="flex items-center gap-1">
                        <Timer className="w-3 h-3" />
                        <span>盲注每 {config.blindIncreaseInterval} 分钟增长</span>
                      </div>
                      <div>
                        起始筹码: {config.startingChips?.toLocaleString()}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50">
        <h3 className="text-sm font-medium text-slate-400 mb-2">当前筹码</h3>
        <p className="text-2xl font-bold text-emerald-400">{currentChips.toLocaleString()}</p>
      </div>
    </div>
  );
}
